// Every point of the turn goes through here: run.step gets the detail, then every hook runs, in order and awaited.
// A hook that throws becomes an error of the run, and the hooks after it still run. It returns the step as the hooks
// left it, which is how a before event changes what happens next and an after event changes what came out.
// Tools may run in parallel, so each event keeps its own step: a hook changes the fields of run.step, never replaces
// it, and reads it before its own first await
export async function emit(run: PlaybookRun, event: PlaybookEvent, step: PlaybookStep = {}): Promise<PlaybookStep> {
  const detail: PlaybookStep = { flow: run.state.cursor?.flow, item: run.state.cursor?.item, ...step }
  for (const hook of run.hooks || []) {
    run.step = detail
    try { await hook(event, run) }
    catch (e) { if (event !== "playbook.error") await fail(run, e, `the ${event} hook failed`) }
  }
  run.step = detail
  return detail
}

// A real failure: the turn goes on with what it has and the outer ring decides what the contact sees
export async function fail(run: PlaybookRun, e: any, where?: string) {
  const message = [where, e?.message || String(e)].filter(Boolean).join(": ")
  run.errors.push({ message, flow: run.state.cursor?.flow, item: run.state.cursor?.item, stack: e?.stack })
  await emit(run, "playbook.error", { note: message })
}

// Every tool goes through here, whoever runs it. tool.before may change the args, or set a result and skip the tool;
// tool.after may change the result. A failing tool must not cost the turn: the model reads the error and the run keeps it
export async function callTool(run: PlaybookRun, name: string, args: any, execute: (args: any) => Promise<any>) {
  const before = await emit(run, "tool.before", { call: { name, args } })
  const call = before.call || { name, args }
  let result = call.result
  if (result === undefined) {
    try { result = await execute(call.args) }
    catch (e: any) {
      result = `The tool failed: ${e?.message || e}`
      await fail(run, e, `the ${name} tool failed`)
    }
  }
  const after = await emit(run, "tool.after", { call: { name, args: call.args, result } })
  return after.call?.result
}

// Every write to a var goes through here, whoever makes it: item.set, an option or transition's set and inc, a capture
export async function setVar(run: PlaybookRun, name: string, value: any) {
  run.state.vars[name] = value
  await emit(run, "var.set", { var: name, value })
}

// Leaves every event on run.trace with its step, copied at the moment it happened. A json copy, so the tools inside
// an ai call (functions) are left out
export const traceHook: PlaybookHook<PlaybookTracedRun> = (event, run) => {
  run.trace ||= []
  run.trace.push({ seq: run.trace.length, event, ...JSON.parse(JSON.stringify(run.step ?? {})) })
}

export const consoleHook: PlaybookHook = (event, run) => {
  console.log(`[playbook] ${event}`, run.step)
}
